import _ from 'lodash';
import type { z } from 'zod';
import { type TransformerConfig } from '../common/types';
import {
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { buildRecordId, encodeRecordId } from '../recordIds';
import { DBVideoCategorySchema, VideoCategorySchema } from './schemas';

type DBVideoCategoryTopic = z.infer<typeof DBVideoCategorySchema>['topics'][number];
type VideoCategoryTopic = z.infer<typeof VideoCategorySchema>['topics'][number];

export const VideoCategoryCodecConfig: TransformerConfig<
  typeof VideoCategorySchema,
  typeof DBVideoCategorySchema,
  'id'
> = {
  name: 'VideoCategory',
  clientSchema: VideoCategorySchema,
  dbSchema: DBVideoCategorySchema,
  transformers: [
    createRenameTransformer<typeof VideoCategorySchema, typeof DBVideoCategorySchema>(
      'code',
      'category'
    ),

    // A topic is stored without its id, subject or category, which its category holds, and
    // its code as key
    createTransformer<typeof VideoCategorySchema, typeof DBVideoCategorySchema>({
      encoder: {
        sourceFields: ['topics'] as const,
        encode: ({ topics }) => ({
          topics: (topics as VideoCategoryTopic[]).map(
            ({ id, subject, categoryCode, code, ...topic }) => ({ key: code, ...topic })
          ),
        }),
      },
      decoder: {
        sourceFields: ['subject', 'category', 'topics'] as const,
        decode: ({ subject, category, topics }) => ({
          ...(subject !== undefined && { subject }),
          // Each topic's id needs its category's subject and code, so a read without
          // them gives no topics
          ...(subject !== undefined &&
            category !== undefined &&
            topics !== undefined && {
              topics: (topics as (DBVideoCategoryTopic | null)[]).map(topic => ({
                ..._.omit(topic, 'key'),
                // A stored entry that is not an object reads with its parts alone, for
                // the read's check to report, rather than failing the decoder
                code: topic?.key as string,
                id: encodeRecordId('videoTopic', {
                  subject,
                  categoryCode: category,
                  code: topic?.key,
                }),
                subject,
                categoryCode: category,
              })) as VideoCategoryTopic[],
            }),
        }),
      },
    }),
  ],
  derived: { id: category => buildRecordId('videoCategory', category) },
};

export const encodeVideoCategoryToDb = createEncodeToDbFn(VideoCategoryCodecConfig);
export const decodeVideoCategoryFromDb = createDecodeFromDbFn(VideoCategoryCodecConfig);
